/**
 * @voltxampere/web — `<vxa-onboarding-tour>` (Sprint 23 C2 + ADR-0046)
 *
 * Hibrit modal + coachmark onboarding tour. Lit component, signal-
 * driven render. Modal step → fixed centered card + dim backdrop.
 * Coachmark step → SVG overlay hole at target's bounding rect +
 * tooltip flow-positioned next to it.
 *
 * Keyboard:
 *   - Esc       → skip
 *   - Enter / → → advance
 *   - ←        → retreat
 *
 * Reduced-motion: `prefers-reduced-motion: reduce` aktifken fade-in
 * + spotlight ease-in animation'ları instant geçişe çevrilir
 * (CSS @media query).
 *
 * ARIA: modal `role="dialog" aria-modal="true" aria-labelledby`,
 * coachmark `role="status"` (assistive technology için live region).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";

import { SignalController } from "../lit-utils/signal-controller.js";
import { t } from "../i18n/index.js";
import {
  advanceStep,
  retreatStep,
  skipTour,
  TOTAL_TOUR_STEPS,
  tourOpen,
  tourStep,
} from "../onboarding/tour-state.js";
import { TOUR_STEPS } from "../onboarding/tour-steps.js";

interface TargetRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

@customElement("vxa-onboarding-tour")
export class VxaOnboardingTour extends LitElement {
  private readonly _open = new SignalController(this, tourOpen);
  private readonly _step = new SignalController(this, tourStep);

  static override styles = css`
    :host {
      display: contents;
    }

    .root {
      position: fixed;
      inset: 0;
      z-index: 9999;
      pointer-events: auto;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: rgba(8, 12, 23, 0.62);
      backdrop-filter: blur(2px);
    }

    .modal-card {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: min(440px, 88vw);
      /* Sprint 26 Lane 7 R5-VIS-7 — canonical horizontal 32 (was 28, 8-pt grid). */
      padding: 32px 32px 24px;
      background: var(--ink, #080c17);
      border: 1px solid var(--brass, #c08a3e);
      border-radius: 12px;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
      color: var(--ivory, #f3ead5);
      font-family: var(--font-body, "EB Garamond", serif);
    }

    .modal-card h2 {
      margin: 0 0 12px;
      font-family: var(--font-display, "Cormorant Infant", serif);
      font-weight: 400;
      font-size: 28px;
      color: var(--brass, #c08a3e);
    }

    .modal-card p {
      margin: 0 0 24px;
      font-size: 16px;
      line-height: 1.5;
      color: var(--ivory-faint, rgba(243, 234, 213, 0.85));
    }

    .step-counter {
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 11px;
      letter-spacing: 0.2em;
      color: var(--ivory-dim, rgba(243, 234, 213, 0.55));
      text-transform: uppercase;
      margin-bottom: 12px;
    }

    .actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    button {
      padding: 8px 16px;
      background: transparent;
      border: 1px solid rgba(243, 234, 213, 0.25);
      border-radius: 6px;
      color: var(--ivory, #f3ead5);
      font-family: var(--font-mono, "JetBrains Mono", monospace);
      font-size: 12px;
      cursor: pointer;
      transition: border-color 150ms ease;
    }

    button:hover {
      border-color: var(--brass, #c08a3e);
    }

    button.primary {
      background: var(--brass, #c08a3e);
      border-color: var(--brass, #c08a3e);
      color: var(--ink, #080c17);
    }

    button.primary:hover {
      background: rgba(192, 138, 62, 0.85);
    }

    button.skip {
      margin-right: auto;
      color: var(--ivory-dim, rgba(243, 234, 213, 0.55));
    }

    .spotlight-svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
    }

    .coachmark-tooltip {
      position: absolute;
      width: min(360px, 80vw);
      padding: 20px 22px 18px;
      background: var(--ink, #080c17);
      border: 1px solid var(--brass, #c08a3e);
      border-radius: 10px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
      color: var(--ivory, #f3ead5);
      font-family: var(--font-body, "EB Garamond", serif);
    }

    .coachmark-tooltip h3 {
      margin: 0 0 10px;
      font-family: var(--font-display, "Cormorant Infant", serif);
      font-weight: 400;
      font-size: 22px;
      color: var(--brass, #c08a3e);
    }

    .coachmark-tooltip p {
      margin: 0 0 16px;
      font-size: 15px;
      line-height: 1.45;
      color: var(--ivory-faint, rgba(243, 234, 213, 0.85));
    }

    @media (prefers-reduced-motion: reduce) {
      .modal-card,
      .coachmark-tooltip {
        transition: none !important;
      }
    }
  `;

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("keydown", this._handleKey);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this._handleKey);
  }

  private readonly _handleKey = (e: KeyboardEvent): void => {
    if (!this._open.value) return;
    // Sprint 24 A0 (bug_006 fix) — editable target guard. Without
    // this, document-level keydown swallows Enter / Arrow keys
    // even when focus is in an input / textarea / contenteditable
    // element. Sprint 23 C2'de Cmd+K palette içinde Enter palette
    // execute yerine tour advance yapan UX kırılmasına yol açtı —
    // step 6 ("Cmd+K ile her şey") kullanıcıya öğrettiği şeyi
    // bozuyordu. Mirrors the same guard ShortcutManager.handleKeyDown
    // uses (apps/web/src/shortcuts/index.ts).
    const target = e.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      skipTour();
      return;
    }
    if (e.key === "Enter" || e.key === "ArrowRight") {
      e.preventDefault();
      advanceStep();
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      retreatStep();
    }
  };

  private _resolveTarget(selectors: readonly string[] | undefined): TargetRect | null {
    if (!selectors || selectors.length === 0) return null;
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el instanceof HTMLElement) {
        const rect = el.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          return { x: rect.left, y: rect.top, width: rect.width, height: rect.height };
        }
      }
    }
    return null;
  }

  override render(): TemplateResult | typeof nothing {
    if (!this._open.value) return nothing;
    const stepIdx = this._step.value;
    const step = TOUR_STEPS[stepIdx];
    if (!step) return nothing;

    const counter = `${String(stepIdx + 1)} / ${String(TOTAL_TOUR_STEPS)}`;
    const isLast = stepIdx >= TOTAL_TOUR_STEPS - 1;
    const isFirst = stepIdx === 0;

    let body: TemplateResult;
    if (step.kind === "coachmark") {
      const rect = this._resolveTarget(step.targetSelectors);
      if (rect) {
        body = this._renderCoachmark(step.titleKey, step.bodyKey, rect, counter, isFirst, isLast);
      } else {
        // Target missing → modal fallback.
        body = this._renderModal(step.titleKey, step.bodyKey, counter, isFirst, isLast);
      }
    } else {
      body = this._renderModal(step.titleKey, step.bodyKey, counter, isFirst, isLast);
    }

    return html`
      <div class="root" data-testid="onboarding-tour" role="presentation">
        <div
          class="backdrop"
          @click=${() => {
            skipTour();
          }}
        ></div>
        ${body}
      </div>
    `;
  }

  private _renderModal(
    titleKey: string,
    bodyKey: string,
    counter: string,
    isFirst: boolean,
    isLast: boolean,
  ): TemplateResult {
    const titleId = `tour-modal-title-${counter.replace(/\s/g, "")}`;
    return html`
      <div
        class="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby=${titleId}
        @click=${(e: Event) => {
          e.stopPropagation();
        }}
      >
        <div class="step-counter">${counter}</div>
        <h2 id=${titleId}>${t(titleKey as never)}</h2>
        <p>${t(bodyKey as never)}</p>
        <div class="actions">
          <button
            class="skip"
            data-testid="tour-skip"
            title=${t("tour.skip") + " (Esc)"}
            @click=${() => {
              skipTour();
            }}
          >
            ${t("tour.skip")}
          </button>
          ${isFirst
            ? nothing
            : html`<button
                data-testid="tour-prev"
                title=${t("tour.prev") + " (←)"}
                @click=${() => {
                  retreatStep();
                }}
              >
                ${t("tour.prev")}
              </button>`}
          <button
            class="primary"
            data-testid="tour-next"
            title=${(isLast ? t("tour.done") : t("tour.next")) + " (→)"}
            @click=${() => {
              advanceStep();
            }}
          >
            ${isLast ? t("tour.done") : t("tour.next")}
          </button>
        </div>
      </div>
    `;
  }

  private _renderCoachmark(
    titleKey: string,
    bodyKey: string,
    rect: TargetRect,
    counter: string,
    isFirst: boolean,
    isLast: boolean,
  ): TemplateResult {
    // Tooltip placement: prefer right side if room, else bottom.
    const margin = 16;
    const tooltipWidth = 360;
    const placeRight = rect.x + rect.width + margin + tooltipWidth < window.innerWidth;
    const tooltipLeft = placeRight ? rect.x + rect.width + margin : rect.x;
    const tooltipTop = placeRight ? rect.y : rect.y + rect.height + margin;

    const titleId = `tour-coachmark-title-${counter.replace(/\s/g, "")}`;
    const padding = 8;

    return html`
      <svg class="spotlight-svg" preserveAspectRatio="none">
        <defs>
          <mask id="tour-spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            <rect
              x=${rect.x - padding}
              y=${rect.y - padding}
              width=${rect.width + padding * 2}
              height=${rect.height + padding * 2}
              rx="6"
              fill="black"
            />
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(8,12,23,0.62)"
          mask="url(#tour-spotlight-mask)"
        />
        <rect
          x=${rect.x - padding}
          y=${rect.y - padding}
          width=${rect.width + padding * 2}
          height=${rect.height + padding * 2}
          rx="6"
          fill="none"
          stroke="var(--brass, #c08a3e)"
          stroke-width="2"
        />
      </svg>
      <div
        class="coachmark-tooltip"
        role="status"
        aria-labelledby=${titleId}
        style=${`left: ${String(tooltipLeft)}px; top: ${String(tooltipTop)}px;`}
        @click=${(e: Event) => {
          e.stopPropagation();
        }}
      >
        <div class="step-counter">${counter}</div>
        <h3 id=${titleId}>${t(titleKey as never)}</h3>
        <p>${t(bodyKey as never)}</p>
        <div class="actions">
          <button
            class="skip"
            data-testid="tour-skip"
            title=${t("tour.skip") + " (Esc)"}
            @click=${() => {
              skipTour();
            }}
          >
            ${t("tour.skip")}
          </button>
          ${isFirst
            ? nothing
            : html`<button
                data-testid="tour-prev"
                title=${t("tour.prev") + " (←)"}
                @click=${() => {
                  retreatStep();
                }}
              >
                ${t("tour.prev")}
              </button>`}
          <button
            class="primary"
            data-testid="tour-next"
            title=${(isLast ? t("tour.done") : t("tour.next")) + " (→)"}
            @click=${() => {
              advanceStep();
            }}
          >
            ${isLast ? t("tour.done") : t("tour.next")}
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-onboarding-tour": VxaOnboardingTour;
  }
}
